import { clearCache, useRequest } from 'ahooks';
import { useEffect, useMemo, useState } from 'react';

import { useI18nContext } from '@/i18n/I18n';
import { IMemoUserInfo } from '@/pages/security/types';
import { clearFetchCache } from '@/services/request';
import { getAvatarInfo } from '@/services/security';
import { IBaseResponse } from '@/services/types/common';

const CACHE_KEY = 'memberInfo';

const COPY_TRADING_PRO = 9;

export function useMemberInfo() {
  const [$t] = useI18nContext();
  const { data, error, loading, refresh } = useRequest<
    IBaseResponse<IMemoUserInfo>,
    []
  >(getAvatarInfo as any, {
    cacheKey: CACHE_KEY,
    staleTime: -1, // data is considered fresh so we call this api only once
    cacheTime: -1, // never clear cache so we call this api only once
  });

  const [avatar, setAvatar] = useState({
    avatar_id: 0,
    avatar_url: '',
  });

  const memberInfo = useMemo(() => {
    return data?.result || {};
  }, [data]) as IMemoUserInfo;

  const updateMemberInfo = (
    cb?: (profile: IBaseResponse<IMemoUserInfo>) => void,
  ) => {
    clearFetchCache('memberInfo');
    clearCache(CACHE_KEY);
    refresh();
    cb && cb(data as IBaseResponse<IMemoUserInfo>);
  };

  const hasCopyTradeBrokerTag = useMemo(() => {
    return memberInfo?.source?.includes('broker_copytrading_tag');
  }, [memberInfo]);

  const copyTradeUpgradeSuccess = useMemo(() => {
    return memberInfo?.copytradeUpgrade === 'SUCCESS';
  }, [memberInfo]);

  useEffect(() => {
    setAvatar({
      avatar_id: memberInfo?.avatar?.avatarId || 0,
      avatar_url: memberInfo?.avatar?.avatarUrl || '',
    });
  }, [memberInfo]);

  const copyTraderInfo = useMemo(() => {
    if (memberInfo !== null && memberInfo?.copytradeUpgrade === 'SUCCESS') {
      return {
        nick: memberInfo.copytradeNick || $t('child.masterTrader'),
        photo: memberInfo.copytradePhoto || '',
      };
    }
    return { nick: '', photo: '' };
  }, [memberInfo, $t]);

  const copyTradingPro = useMemo(() => {
    return memberInfo?.memberType === COPY_TRADING_PRO;
  }, [memberInfo]);

  const subMemberBind2faSkipFirstStep = useMemo(() => {
    return memberInfo.subMember2faVerify === 1;
  }, [memberInfo]);

  return {
    error,
    loading,
    memberInfo,
    updateMemberInfo,
    hasCopyTradeBrokerTag,
    copyTradeUpgradeSuccess,
    avatar,
    setAvatar,
    copyTradingPro,
    copyTraderInfo,
    subMemberBind2faSkipFirstStep,
  };
}
